import { useQuery } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { AuditLog, AuditLogQueryParams, PaginatedResponse } from '@/models/audit';

// Get audit logs with optional filtering
export const useAuditLogs = (params: AuditLogQueryParams = {}) => {
    return useQuery<PaginatedResponse<AuditLog>>({
        queryKey: ['audit-logs', params],
        queryFn: async () => {
            const { data } = await httpClient.get<PaginatedResponse<AuditLog>>(
                API_ENDPOINTS.CRM.AUDIT_LOGS.LIST,
                { params }
            );
            return data;
        },
    });
};

// Get a specific audit log by ID
export const useAuditLog = (logId: string) => {
    return useQuery<ApiResponse<AuditLog>>({
        queryKey: ['audit-logs', logId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<AuditLog>>(
                API_ENDPOINTS.CRM.AUDIT_LOGS.DETAILS(logId)
            );
            return data;
        },
        enabled: !!logId,
    });
};

// Export audit logs to CSV
export const exportAuditLogs = async (params: AuditLogQueryParams = {}) => {
    try {
        const response = await httpClient.get(API_ENDPOINTS.CRM.AUDIT_LOGS.EXPORT, {
            params,
            responseType: 'blob',
        });

        // Create a blob URL from the response data
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link element to trigger the download
        const link = document.createElement('a');
        link.href = url;

        // Try to get the filename from the Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'audit_logs.csv';

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1];
            }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return true;
    } catch (error) {
        console.error('Error exporting audit logs:', error);
        throw error;
    }
};
